<template>
  <div class="pa-4">
    <div class="text-h6 mb-2">Organizations</div>

    <!-- Active orgs are managed here; archived (soft-deleted) orgs live in their own
         tab so they don't clutter the live list. -->
    <v-tabs v-model="tab" density="compact" class="mb-4">
      <v-tab value="active" data-cy="orgs-tab-active">Active ({{ activeOrgs.length }})</v-tab>
      <v-tab value="archived" data-cy="orgs-tab-archived">Archived ({{ archivedOrgs.length }})</v-tab>
    </v-tabs>

    <!-- Create organization (active view only) -->
    <v-card v-if="tab === 'active'" class="mb-6 pa-4" variant="outlined">
      <div class="text-subtitle-2 font-weight-bold mb-2">Create organization</div>
      <div class="d-flex align-center ga-3">
        <v-text-field
          v-model="newOrgName"
          label="Organization name"
          data-cy="new-org-name-input"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 360px"
          @keyup.enter="createOrg"
        />
        <v-btn color="primary" variant="flat" :loading="creating" :disabled="!newOrgName.trim()" data-cy="create-org-btn" @click="createOrg">
          Create
        </v-btn>
      </div>
    </v-card>

    <div class="text-caption text-medium-emphasis mb-4">
      <template v-if="tab === 'active'">Click an organization to manage it. Workflow counts are coming soon.</template>
      <template v-else>Archived organizations are hidden from members and blocked by access controls. Restore one to bring it back.</template>
    </div>

    <div v-if="displayedOrgs.length === 0" class="text-body-2 text-medium-emphasis">
      <template v-if="isSearching">No organizations match &ldquo;{{ search }}&rdquo;.</template>
      <template v-else-if="tab === 'active'">No active organizations yet.</template>
      <template v-else>No archived organizations.</template>
    </div>

    <v-card
      v-for="org in displayedOrgs"
      :key="org.id"
      class="mb-3 pa-4 org-row"
      variant="outlined"
      @click="openOrg(org.id)"
    >
      <div class="d-flex align-center flex-wrap ga-4">
        <div class="org-name-col">
          <div class="text-subtitle-1 font-weight-bold text-primary">
            {{ org.name }}
            <v-chip v-if="org.deletedAt" size="x-small" color="warning" variant="tonal" class="ml-1">Archived</v-chip>
          </div>
          <div class="text-caption text-medium-emphasis">
            <template v-if="ownersForOrg(org.id).length">
              Owner: {{ ownersForOrg(org.id).map((o) => o.email || o.userId).join(', ') }}
            </template>
            <template v-else-if="membersLoaded(org.id)">
              <v-chip size="x-small" color="warning" variant="tonal" class="mr-1">No owner</v-chip>
              <a href="#" class="set-owner-link" @click.stop.prevent="setOwner(org.id)">Set owner</a>
            </template>
          </div>
        </div>

        <v-spacer />

        <div class="d-flex ga-8 text-center">
          <div>
            <div class="text-subtitle-1 font-weight-medium">{{ userCount(org.id) }}</div>
            <div class="text-caption text-medium-emphasis">Users</div>
          </div>
          <div>
            <div class="text-subtitle-1 font-weight-medium">{{ projectCount(org) }}</div>
            <div class="text-caption text-medium-emphasis">Projects</div>
          </div>
          <div>
            <div class="text-subtitle-1 font-weight-medium text-disabled">—</div>
            <div class="text-caption text-medium-emphasis">Workflows</div>
          </div>
        </div>

        <template v-if="org.deletedAt">
          <v-btn
            size="small"
            variant="tonal"
            color="primary"
            :loading="restoringId === org.id"
            @click.stop="restoreOrg(org)"
          >
            Restore
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            color="error"
            @click.stop="openPurge(org)"
          >
            Delete permanently
          </v-btn>
        </template>

        <v-icon class="text-medium-emphasis">mdi-chevron-right</v-icon>
      </div>
    </v-card>

    <!-- Permanent-delete confirmation (snapshots to S3, then cascade hard-deletes). -->
    <v-dialog v-model="purgeDialog" max-width="520">
      <v-card class="pa-6">
        <div class="text-h6 mb-1 text-error">Permanently delete organization</div>
        <div class="text-body-2 text-medium-emphasis mb-4">
          This permanently deletes <strong>{{ purgeTarget?.name }}</strong> and all of its projects,
          workflows, members, and AI-usage records. A JSON snapshot is saved to S3 first, but this
          <strong>cannot be undone from the app</strong>.
        </div>
        <v-text-field
          v-model="purgeConfirmName"
          label="Type the organization name to confirm"
          :placeholder="purgeTarget?.name"
          variant="outlined"
          density="compact"
          :error="!!purgeConfirmName && purgeConfirmName !== purgeTarget?.name"
        />
        <div class="d-flex justify-end ga-2 mt-2">
          <v-btn variant="outlined" @click="purgeDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :disabled="purgeConfirmName !== purgeTarget?.name"
            :loading="purging"
            @click="confirmPurge"
          >
            Permanently delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

// `search` comes from the shared admin-page search bar so one search filters both the
// Registered Users table and this org list (the bar is rendered by the parent page).
const props = defineProps({
  search: { type: String, default: '' }
})

const store = useStore()
const router = useRouter()

const organisations = computed(() => store.state.organisations.organisations || [])

// Active vs archived (soft-deleted) split — kept in separate tabs so archived orgs
// don't clutter the live management list.
const tab = ref('active')
const activeOrgs = computed(() => organisations.value.filter((o) => !o.deletedAt))
const archivedOrgs = computed(() => organisations.value.filter((o) => o.deletedAt))

// Search (from the shared admin search bar) + alphabetical sort so a long list (many
// like-named test orgs) is navigable.
const matchesSearch = (o) => {
  const q = (props.search || '').trim().toLowerCase()
  if (!q) {
    return true
  }

  return (o.name || '').toLowerCase().includes(q) || (o.id || '').toLowerCase().includes(q)
}
const displayedOrgs = computed(() => {
  const base = tab.value === 'archived' ? archivedOrgs.value : activeOrgs.value

  return base
    .filter(matchesSearch)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})
const isSearching = computed(() => (props.search || '').trim().length > 0)

const newOrgName = ref('')
const creating = ref(false)
const restoringId = ref(null)

// Restore an archived (soft-deleted) org — clears deletedAt so it's visible and
// accessible again. SuperAdmin-only (enforced at the backend).
const restoreOrg = async (org) => {
  restoringId.value = org.id
  await store.dispatch('organisations/restoreOrganisation', { id: org.id })
  restoringId.value = null
}

// Permanent delete (snapshot → cascade hard-delete) — guarded by a type-the-name dialog.
const purgeDialog = ref(false)
const purgeTarget = ref(null)
const purgeConfirmName = ref('')
const purging = ref(false)

const openPurge = (org) => {
  purgeTarget.value = org
  purgeConfirmName.value = ''
  purgeDialog.value = true
}

const confirmPurge = async () => {
  if (!purgeTarget.value || purgeConfirmName.value !== purgeTarget.value.name) {
    return
  }
  purging.value = true
  await store.dispatch('organisations/purgeOrganisation', { id: purgeTarget.value.id })
  purging.value = false
  purgeDialog.value = false
  purgeTarget.value = null
}

const ownersForOrg = (orgId) =>
  store.getters['orgMembers/getMembersByOrgId'](orgId).filter((m) => m.role === 'OWNER')

// Have this org's members finished loading? (avoids flashing "No owner" first)
const membersLoaded = (orgId) => store.state.orgMembers.membersByOrg[orgId] !== undefined

// Jump straight to the org's Members tab to promote someone to OWNER.
const setOwner = (orgId) => {
  router.push(`/org/${orgId}/admin?from=admin&tab=members`)
}

// User count comes from loaded OrgMember rows; "—" until that org's members load.
// Project/workflow counts need a backend metrics query (not available client-side
// for orgs the viewer isn't a member of) — shown as "—" for now.
const userCount = (orgId) => {
  const members = store.state.orgMembers.membersByOrg[orgId]

  return members ? members.length : '—'
}

// Project count comes from the org's server-resolved `projects` field
// (SuperAdmin-gated) on listOrganisations — no extra backend resource needed.
const projectCount = (org) => (org.projects ? org.projects.length : '—')

const loadMembers = async () => {
  await Promise.all(
    organisations.value.map((org) => store.dispatch('orgMembers/listOrgMembers', { orgId: org.id }))
  )
}

onMounted(async () => {
  if (!store.state.organisations.organisations) {
    await store.dispatch('organisations/listOrganisations')
  }
  await loadMembers()
})

const openOrg = (orgId) => {
  router.push(`/org/${orgId}/admin?from=admin`)
}

const createOrg = async () => {
  const name = newOrgName.value.trim()
  if (!name) {
    return
  }
  creating.value = true
  const id = await store.dispatch('organisations/createOrganisation', { name })
  if (id) {
    newOrgName.value = ''
    await store.dispatch('organisations/listOrganisations')
    await loadMembers()
  }
  creating.value = false
}
</script>

<style scoped>
.org-row {
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.org-row:hover {
  border-color: rgba(var(--v-theme-primary), 0.5);
  background: rgba(var(--v-theme-primary), 0.03);
}
.org-name-col {
  min-width: 220px;
}
</style>
